/**
 * Relationship Chat API Service
 *
 * API client functions for relationship tracker AI chat sessions.
 * Supports both blocking and streaming (SSE) message modes.
 */

import { apiClient, getCurrentAccessToken } from "./api";
import type {
  RelationshipChat,
  RelationshipChatCreate,
  RelationshipChatMessage,
  RelationshipChatMessageCreate,
} from "@/types/relationshipChat";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";
const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Create a new relationship chat session
 */
export async function createChat(
  data: RelationshipChatCreate
): Promise<RelationshipChat> {
  const response = await apiClient.post(
    `${API_PREFIX}/relationship-chat`,
    data
  );
  return response.data;
}

/**
 * Get a relationship chat session with messages
 */
export async function getChat(chatId: string): Promise<RelationshipChat> {
  const response = await apiClient.get(
    `${API_PREFIX}/relationship-chat/${chatId}`
  );
  return response.data;
}

/**
 * Delete a relationship chat session
 */
export async function deleteChat(chatId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/relationship-chat/${chatId}`);
}

/**
 * Send a message (blocking)
 */
export async function sendMessage(
  chatId: string,
  message: RelationshipChatMessageCreate
): Promise<RelationshipChatMessage> {
  const response = await apiClient.post(
    `${API_PREFIX}/relationship-chat/${chatId}/messages`,
    message
  );
  return response.data;
}

/**
 * Callback types for streaming message events
 */
export interface StreamingCallbacks {
  onUserMessage?: (message: RelationshipChatMessage) => void;
  onContent?: (content: string) => void;
  onDone?: (message: RelationshipChatMessage) => void;
  onError?: (error: string) => void;
}

/**
 * Send a message with streaming response via SSE
 */
export async function sendMessageStreaming(
  chatId: string,
  message: RelationshipChatMessageCreate,
  callbacks: StreamingCallbacks
): Promise<void> {
  const token = await getCurrentAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE_URL}${API_PREFIX}/relationship-chat/${chatId}/messages/stream`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(message),
      credentials: "include",
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send message: ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  const processSSELines = (lines: string[]) => {
    let currentEvent = "";
    let currentData = "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7);
      } else if (line.startsWith("data: ")) {
        currentData = line.slice(6);
      } else if (line === "" && currentEvent && currentData) {
        try {
          const data = JSON.parse(currentData);

          switch (currentEvent) {
            case "user_message":
              callbacks.onUserMessage?.(data);
              break;
            case "content":
              callbacks.onContent?.(data.content);
              break;
            case "done":
              callbacks.onDone?.(data);
              break;
            case "error":
              callbacks.onError?.(data.error);
              break;
          }
        } catch (e) {
          console.error("Failed to parse SSE data:", e);
        }

        currentEvent = "";
        currentData = "";
      }
    }
  };

  try {
    let reading = true;
    while (reading) {
      const { done, value } = await reader.read();

      if (done) {
        reading = false;
      }

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      processSSELines(lines);
    }

    if (buffer) {
      const finalLines = buffer.split("\n");
      finalLines.push("");
      processSSELines(finalLines);
    }
  } finally {
    reader.releaseLock();
  }
}
